import { Widget } from '@/types/widget'
import { supabase } from '@/integrations/supabase/client'
import { getApiBaseUrl } from '@/lib/api'

// Helper to get auth headers
export const getAuthHeaders = async () => {
	const {
		data: { session },
	} = await supabase.auth.getSession()
	// Handle case where session might be null (user logged out)
	if (!session) {
		console.warn('getAuthHeaders called without active session.')
		// Decide how to handle this - throw error? return empty headers?
		// Returning basic content-type for now, but requests might fail.
		return { 'Content-Type': 'application/json' }
	}
	return {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${session?.access_token}`,
	}
}

export const widgetApi = {
	// Get all widgets for a team/program
	getWidgets: async (programId?: string) => {
		const params = new URLSearchParams()
		if (programId) params.append('programId', programId)

		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/widgets?${params}`,
			{
				headers,
			}
		)
		if (!response.ok) throw new Error('Failed to fetch widgets')
		return response.json()
	},

	// Get a single widget by ID
	getWidget: async (widgetId: string) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/widgets/${widgetId}`,
			{
				headers,
			}
		)
		if (!response.ok) throw new Error('Failed to fetch widget')
		return response.json()
	},

	// Create a new widget
	createWidget: async (data: Partial<Widget>) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/widgets`,
			{
				method: 'POST',
				headers,
				body: JSON.stringify(data),
			}
		)
		if (!response.ok) throw new Error('Failed to create widget')
		return response.json()
	},

	// Update a widget
	updateWidget: async (widgetId: string, data: Partial<Widget>) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/widgets/${widgetId}`,
			{
				method: 'PUT',
				headers,
				body: JSON.stringify(data),
			}
		)
		if (!response.ok) throw new Error('Failed to update widget')
		return response.json()
	},

	// Delete a widget
	deleteWidget: async (widgetId: string) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/widgets/${widgetId}`,
			{
				method: 'DELETE',
				headers,
			}
		)
		if (!response.ok) throw new Error('Failed to delete widget')
		return response.status === 204 ? null : response.json()
	},

	checkActivationLimits: async (teamId: string, widgetId: string) => {
		const response = await fetch(
			`${getApiBaseUrl()}/api/check-activation-limits?team_id=${teamId}&widget_id=${widgetId}`
		)
		if (!response.ok) {
			throw new Error('Failed to check widget activation limits')
		}
		return response.json()
	},
}

// Webhook API functions
export const webhookApi = {
	// Get all webhooks for a specific program
	getWebhooks: async (programId: string) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/programs/${programId}/webhooks`,
			{
				headers,
			}
		)
		if (!response.ok) throw new Error('Failed to fetch webhooks')
		return response.json()
	},

	// Create a new webhook
	createWebhook: async (programId: string, data: {
		name: string
		url: string
		event_types: string[]
		is_active?: boolean
	}) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/programs/${programId}/webhooks`,
			{
				method: 'POST',
				headers,
				body: JSON.stringify(data),
			}
		)
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new Error(errorData.error || 'Failed to create webhook')
		}
		return response.json()
	},

	// Update an existing webhook
	updateWebhook: async (programId: string, webhookId: string, data: {
		name?: string
		url?: string
		event_types?: string[]
		is_active?: boolean
	}) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/programs/${programId}/webhooks/${webhookId}`,
			{
				method: 'PUT',
				headers,
				body: JSON.stringify(data),
			}
		)
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new Error(errorData.error || 'Failed to update webhook')
		}
		return response.json()
	},

	// Delete a webhook
	deleteWebhook: async (programId: string, webhookId: string) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/programs/${programId}/webhooks/${webhookId}`,
			{
				method: 'DELETE',
				headers,
			}
		)
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new Error(errorData.error || 'Failed to delete webhook')
		}
		return response.status === 204 ? null : response.json()
	},

	// Test a webhook
	testWebhook: async (programId: string, webhookId: string) => {
		const headers = await getAuthHeaders()
		const response = await fetch(
			`${getApiBaseUrl()}/api/programs/${programId}/webhooks/${webhookId}/test`,
			{
				method: 'POST',
				headers,
			}
		)
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}))
			throw new Error(errorData.error || 'Failed to test webhook')
		}
		return response.json()
	},
}
